/** Makes it easier to identify an async request on the server */
export const asyncHeaders = { 'X-Requested-With': 'XMLHttpRequest' };

type GetRequestData = ConstructorParameters<typeof URLSearchParams>[0];

export function get(data: GetRequestData) {
    return fetch(resolveUrl(null, data), {headers: asyncHeaders});
}

export function getFrom(url: string, data?: GetRequestData) {
    return fetch(resolveUrl(url, data), {headers: asyncHeaders});
}

export function post(data: BodyInit) {
    return postTo(resolveUrl(), data);
}

export function postTo(url: string, data?: BodyInit) {
    return request(url, 'POST', data);
}

export function deleteRequest(url: string) {
    return request(url, 'DELETE');
}

function request(url: string, method: 'GET' | 'POST' | 'DELETE', data?: BodyInit) {
    return fetch(url, {
        method: method,
        body: data,
        headers: asyncHeaders,
    });
}

export const asText = (r: Response) => r.text();

export const decodeJson = (r: Response) => r.json();

export function resolveUrl(url?: string, data?: ConstructorParameters<typeof URLSearchParams>[0]) {
    // Note: removes the # symbol (and any subsequent characters) from the url if it exists
    let result = url || window.location.href.split('#')[0];
    if (data) {
        result = withUrlParams(result, new URLSearchParams(data));
    }
    return result;
}

export function withPageParams(url: string) {
    return withUrlParams(url, getPageParams());
}

/** 
 * Gets a snapshot of the current page params. 
 * Warning: the returned object does not update automatically when the url changes.
 **/
export function getPageParams() {
    return new URLSearchParams(window.location.search);
}

export function withUrlParams(url: string, params: URLSearchParams) {
    const [endpoint, queryString] = url.split('?');
    const endpointParams = new URLSearchParams(queryString);
    // Add new params, overwriting existing ones with the same key
    for (const [key, value] of params) {
        endpointParams.set(key, value);
    }
    return formatUrl(endpoint, endpointParams);
}

export function removeUrlParams(url: string): [string, Record<string, any>] {
    const [endpoint, queryString] = url.split('?');
    const params = new URLSearchParams(queryString);
    return [endpoint, convertUrlParams(params)];
}

export function convertUrlParams(params: URLSearchParams) {
    let result: Record<string, any> = {}
    for (let [key, value] of params) {
        result[key] = value;
    }
    return result;
}

function formatUrl(endpoint: string, params: URLSearchParams) {
    const queryString = params.toString();
    return queryString ? `${endpoint}?${queryString}` : endpoint;
}

export type VoidFunctionWithAnyArgs = (...args: any[]) => void;

export function debounce<F extends VoidFunctionWithAnyArgs>(func: F, wait: number, immediate: boolean = false): (this: ThisParameterType<F>, ...args: Parameters<F>) => void {
    return (<any>window).debounce(func, wait, immediate);
};

export function getOrigin() {
    return (<any>window).getOrigin();
}

export function getNonce(element = document.body): string {
    return element.dataset.nonce;
}